import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import queueAPIsService, { QueueStatusResponse } from "../hooks/queueAPIs";
import AppLoading from "../components/AppLoading";
import Env from "../env/Env";

type JobRow = {
  state: string;
  id: string | number;
  name: string;
  taskLabel?: string;
  progress?: number;
  attemptsMade?: number;
  failedReason?: string;
  timestamp?: number;
  processedOn?: number;
  finishedOn?: number;
};

type ExtraJobColumnKey = "id" | "progress" | "attempts" | "failedReason";

const QueueStatusPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QueueStatusResponse | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [deletingJobIds, setDeletingJobIds] = useState<Set<string>>(new Set());
  const [requeueingJobIds, setRequeueingJobIds] = useState<Set<string>>(new Set());
  const [resetting, setResetting] = useState(false);
  const [redisHealthy, setRedisHealthy] = useState<boolean | null>(null);
  const [extraJobColumns, setExtraJobColumns] = useState<Set<ExtraJobColumnKey>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await queueAPIsService.getQueueStatus();
      setData(response);
      setLastRefreshedAt(Date.now());
    } catch (e: any) {
      setError(e?.message || "Failed to fetch queue status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadHealth = useCallback(async () => {
    try {
      const base = (Env.API_BASE || "").replace(/\/$/, "");
      const { data: health } = await axios.get(`${base}/health`, { timeout: 15000 });
      const redisServiceStatus =
        (health?.services?.redis?.status as string | undefined) ??
        (health?.services?.redis?.healthy as string | undefined);
      setRedisHealthy(redisServiceStatus === "UP");
    } catch {
      setRedisHealthy(null);
    }
  }, []);

  // Auto-refresh queue status every 15 seconds (same pattern as other settings pages).
  useEffect(() => {
    const interval = setInterval(() => {
      void load();
      void loadHealth();
    }, 15000);
    return () => clearInterval(interval);
  }, [load, loadHealth]);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  const totalVisible = useMemo(() => {
    if (!data?.jobsByState) return 0;
    return (
      data.jobsByState.active.length +
      data.jobsByState.waiting.length +
      data.jobsByState.delayed.length +
      data.jobsByState.failed.length +
      data.jobsByState.completed.length
    );
  }, [data]);

  const counts = data?.counts || {};
  const isExtraColumnEnabled = useCallback(
    (key: ExtraJobColumnKey) => extraJobColumns.has(key),
    [extraJobColumns],
  );

  const toggleExtraColumn = useCallback((key: ExtraJobColumnKey) => {
    setExtraJobColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const allJobs = useMemo<JobRow[]>(() => {
    if (!data?.jobsByState) return [];
    const rows: JobRow[] = [
      ...(data.jobsByState.active || []).map((j) => ({ ...j, state: "active" })),
      ...(data.jobsByState.waiting || []).map((j) => ({ ...j, state: "waiting" })),
      ...(data.jobsByState.delayed || []).map((j) => ({ ...j, state: "delayed" })),
      ...(data.jobsByState.failed || []).map((j) => ({ ...j, state: "failed" })),
      ...(data.jobsByState.completed || []).map((j) => ({ ...j, state: "completed" })),
    ];

    const bestTime = (j: JobRow) => j.finishedOn ?? j.processedOn ?? j.timestamp ?? 0;
    return rows.sort((a, b) => bestTime(b) - bestTime(a));
  }, [data]);

  const deleteJob = useCallback(
    async (job: JobRow) => {
      const id = String(job.id);
      try {
        setDeletingJobIds((prev) => new Set(prev).add(id));
        setError(null);
        await queueAPIsService.deleteJob(job.id);
        await load();
      } catch (e: any) {
        setError(e?.message || "Failed to delete job");
      } finally {
        setDeletingJobIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [load],
  );

  const resetQueue = useCallback(async () => {
    if (
      !window.confirm(
        "Reset the job queue? This removes all active, waiting, failed, and completed jobs. Scheduled repeat jobs (e.g. bucket-clear) are kept.",
      )
    ) {
      return;
    }
    try {
      setResetting(true);
      setError(null);
      await queueAPIsService.resetQueue();
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to reset queue");
    } finally {
      setResetting(false);
    }
  }, [load]);

  const requeueJob = useCallback(
    async (job: JobRow) => {
      const id = String(job.id);
      try {
        setRequeueingJobIds((prev) => new Set(prev).add(id));
        setError(null);
        await queueAPIsService.requeueJob(job.id);
        await load();
      } catch (e: any) {
        setError(e?.message || "Failed to re-enqueue job");
      } finally {
        setRequeueingJobIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [load],
  );

  return (
    <div className="stats-page queue-status-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Queue Status</h2>
          <p>Live status from backend <span className="mono">/api/internal/jobs</span></p>
        </div>
        <div className="queue-controls">
          <button
            className="btn btn-secondary"
            onClick={() => void resetQueue()}
            disabled={loading || resetting}
            title="Clear stuck active/waiting/failed/completed jobs"
          >
            {resetting ? "Resetting..." : "Reset queue"}
          </button>
          <button className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="auto-refresh-status">
        <span className="status-indicator">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="status-icon">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Page Auto-refresh: Every 15 seconds
        </span>
        <div
          className={`queue-health-status ${
            redisHealthy === null ? "unknown" : redisHealthy ? "up" : "down"
          }`}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {redisHealthy ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            )}
          </svg>
          Redis Health:{" "}
          <span className="queue-health-text">
            {redisHealthy === null ? "Unknown" : redisHealthy ? "Healthy" : "Unhealthy"}
          </span>
        </div>
      </div>

      {loading && !data && (
        <div className="loading-container">
          <AppLoading />
        </div>
      )}

      {error && (
        <div className="error-container">
          <div className="error-card">
            <div className="error-content">
              <h2>Error</h2>
              <p>{error}</p>
              <button onClick={() => void load()} className="retry-btn">
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="layout-content">
          <div className="cache-overview-section">
            <div className="section-header">
              <h2>Overview</h2>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container blue">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Waiting</dt>
                        <dd>{String(counts.waiting ?? data.jobsByState?.waiting?.length ?? 0)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container green">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Active</dt>
                        <dd>{String(counts.active ?? data.jobsByState?.active?.length ?? 0)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container purple">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Delayed</dt>
                        <dd>{String(counts.delayed ?? data.jobsByState?.delayed?.length ?? 0)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container red">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Failed</dt>
                        <dd>{String(counts.failed ?? data.jobsByState?.failed?.length ?? 0)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container teal">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Completed</dt>
                        <dd>{String(counts.completed ?? data.jobsByState?.completed?.length ?? 0)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="keys-section">
            <div className="section-header">
              <h2>Jobs ({allJobs.length})</h2>
              <details className="queue-columns-picker">
                <summary className="queue-columns-picker__summary">Columns</summary>
                <div className="queue-columns-picker__menu">
                  <label className="queue-columns-picker__item">
                    <input
                      type="checkbox"
                      checked={isExtraColumnEnabled("id")}
                      onChange={() => toggleExtraColumn("id")}
                    />
                    <span>ID</span>
                  </label>
                  <label className="queue-columns-picker__item">
                    <input
                      type="checkbox"
                      checked={isExtraColumnEnabled("progress")}
                      onChange={() => toggleExtraColumn("progress")}
                    />
                    <span>Progress</span>
                  </label>
                  <label className="queue-columns-picker__item">
                    <input
                      type="checkbox"
                      checked={isExtraColumnEnabled("attempts")}
                      onChange={() => toggleExtraColumn("attempts")}
                    />
                    <span>Attempts</span>
                  </label>
                  <label className="queue-columns-picker__item">
                    <input
                      type="checkbox"
                      checked={isExtraColumnEnabled("failedReason")}
                      onChange={() => toggleExtraColumn("failedReason")}
                    />
                    <span>Failed reason</span>
                  </label>
                </div>
              </details>
            </div>
            {allJobs.length === 0 ? (
              <div className="queue-empty">No jobs</div>
            ) : (
              <div className="queue-table-scroll">
                <table className="queue-table">
                  <thead>
                    <tr>
                      <th className="queue-table-col-state">State</th>
                      {isExtraColumnEnabled("id") && (
                        <th className="queue-table-col-id queue-table-th-truncate">ID</th>
                      )}
                      <th className="queue-table-col-name queue-table-th-truncate">Name</th>
                      <th className="queue-table-col-task queue-table-th-truncate">Task</th>
                      {isExtraColumnEnabled("progress") && (
                        <th className="queue-table-col-num">Progress</th>
                      )}
                      {isExtraColumnEnabled("attempts") && (
                        <th className="queue-table-col-num">Attempts</th>
                      )}
                      {isExtraColumnEnabled("failedReason") && (
                        <th className="queue-table-col-failed queue-table-th-truncate">Failed reason</th>
                      )}
                      <th className="queue-table-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allJobs.map((job) => {
                      const nameText = String(job.name);
                      const taskText = String(job.taskLabel ?? job.name);
                      return (
                        <tr key={`${job.state}-${job.id}`}>
                        <td>
                          <span className={`queue-state-badge state-${job.state}`}>{job.state}</span>
                        </td>
                        {isExtraColumnEnabled("id") && (
                          <td className="mono queue-table-cell-truncate" title={String(job.id)}>
                            {String(job.id)}
                          </td>
                        )}
                        <td className="queue-table-cell-truncate" title={nameText}>
                          {job.name}
                        </td>
                        <td className="queue-task-label queue-table-cell-truncate" title={taskText}>
                          {taskText}
                        </td>
                        {isExtraColumnEnabled("progress") && <td>{job.progress ?? 0}</td>}
                        {isExtraColumnEnabled("attempts") && <td>{job.attemptsMade ?? 0}</td>}
                        {isExtraColumnEnabled("failedReason") && (
                          <td
                            className={job.failedReason ? "queue-table-cell-truncate" : undefined}
                            title={job.failedReason || undefined}
                          >
                            {job.failedReason ? (
                              <span className="failed-reason-pill" title={job.failedReason}>
                                Failed reason
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                        <td className="queue-actions">
                          {!["completed", "waiting", "active"].includes(job.state) && (
                            <button
                              type="button"
                              className="requeue-btn"
                              title="Re-enqueue job"
                              onClick={() => void requeueJob(job)}
                              disabled={requeueingJobIds.has(String(job.id))}
                            >
                              {requeueingJobIds.has(String(job.id)) ? (
                                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            className="delete-key-btn"
                            title="Remove job"
                            onClick={() => void deleteJob(job)}
                            disabled={deletingJobIds.has(String(job.id))}
                          >
                            {deletingJobIds.has(String(job.id)) ? (
                              <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueStatusPage;
